// src/components/BasePriceEditor.jsx — Voie B+ (prix de base)
//
// Bloc éditeur du prix de base d'un logement (section fiche logement) :
//   - Champ « Prix de base (€/nuit) » pré-rempli avec base_price (ou la
//     suggestion si base_price NULL), sauvegardé via pricing_set_base_price.
//   - Bouton « Appliquer le prix de base » → ouvre un modal :
//       plage du/au + raccourci « tout le futur » (aujourd'hui → horizon)
//       + toggle « remplir les vides seulement » (DÉFAUT) / « écraser aussi
//       les prix auto existants » + récap de confirmation
//       « X dates → Y€ (dont Z déjà tarifées) » (appel confirmStage) AVANT
//       le push réel.
//
// MODE-GATED : champ + bouton actifs UNIQUEMENT si pricing_mode='manual'
// (grisés/disabled + tooltip si pricelabs).

import { useState, useEffect } from "react";
import { DS } from "../lib/ui";
import { useBasePrice } from "../lib/use-base-price";
import useEchapPourFermer from "../lib/useEchapPourFermer"

// Horizon "tout le futur" = aujourd'hui → +18 mois (cohérent cap backend 540j).
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function horizonIso() {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() + 18);
  return d.toISOString().slice(0, 10);
}

export default function BasePriceEditor({ logementId, onApplied }) {
  const {
    basePrice,
    suggestion,
    pricingMode,
    hasMapping,
    loading,
    error,
    save,
    applyBase,
    refetch,
  } = useBasePrice(logementId);

  const readOnlyPricelabs = pricingMode === "pricelabs";
  const disabled = readOnlyPricelabs || !hasMapping;

  // Valeur effective pré-remplie : base_price si défini, sinon suggestion.
  const effectiveValue = basePrice != null ? basePrice : suggestion;

  const [inputValue, setInputValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { kind, msg }
  const [modalOpen, setModalOpen] = useState(false);

  // Sync input ↔ valeur effective post-fetch.
  useEffect(() => {
    if (loading) return;
    setInputValue(effectiveValue == null ? "" : String(effectiveValue));
  }, [effectiveValue, loading]);

  function flashToast(kind, msg, ms = 4000) {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), ms);
  }

  async function handleSave() {
    if (disabled) return;
    let parsed = null;
    if (inputValue !== "" && inputValue != null) {
      const n = Number(inputValue);
      if (!Number.isFinite(n) || n <= 0 || n > 9999) {
        flashToast("err", "Doit être un nombre entre 1 et 9999.");
        return;
      }
      parsed = n;
    }
    setSaving(true);
    const result = await save(parsed);
    setSaving(false);
    if (!result.ok) {
      flashToast("err", `Erreur : ${result.error || "inconnue"}`);
      return;
    }
    flashToast(
      "ok",
      parsed == null
        ? "Prix de base effacé (NULL)."
        : `Prix de base ${parsed}€ enregistré.`,
      5000,
    );
  }

  const parsedBase = (() => {
    const n = Number(inputValue);
    return Number.isFinite(n) && n > 0 && n <= 9999 ? n : null;
  })();

  return (
    <div>
      {readOnlyPricelabs && (
        <div
          style={{
            padding: "8px 11px",
            background: "#fff7e6",
            border: "1px solid #ffd580",
            borderRadius: 7,
            fontSize: 11,
            color: "#856404",
            lineHeight: 1.5,
            marginBottom: 10,
          }}
          title="Le prix de base est dormant en mode PriceLabs : les prix sont calculés automatiquement."
        >
          ⚠️ <strong>Mode PriceLabs actif</strong> — le prix de base est dormant
          (les prix sont calculés automatiquement). Champ &amp; application
          désactivés.
        </div>
      )}
      {!hasMapping && !readOnlyPricelabs && (
        <div
          style={{
            padding: "8px 11px",
            background: "#fff7e6",
            border: "1px solid #ffd580",
            borderRadius: 7,
            fontSize: 11,
            color: "#856404",
            lineHeight: 1.5,
            marginBottom: 10,
          }}
        >
          ⚠️ <strong>Mapping Beds24 manquant</strong> — configurer un roomId
          dans <strong>Paramètres → 🔌 Beds24</strong> avant d'appliquer un prix
          de base.
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: DS.navy }}>
            Prix de base (€/nuit)
          </span>
          <input
            type="number"
            value={inputValue}
            disabled={disabled}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={
              suggestion != null ? `suggéré ${suggestion}€` : "ex: 90"
            }
            style={{
              width: 130,
              padding: "7px 10px",
              borderRadius: 7,
              border: "1px solid #cdd7e3",
              fontSize: 14,
              background: disabled ? "#f1f3f6" : "#fff",
              color: disabled ? "#999" : "#222",
            }}
          />
        </label>
        <button
          type="button"
          onClick={handleSave}
          disabled={disabled || saving}
          style={{
            padding: "8px 14px",
            borderRadius: 7,
            border: "none",
            background: disabled ? "#c4ccd6" : DS.navy,
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            cursor: disabled || saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "…" : "Enregistrer"}
        </button>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          disabled={disabled || parsedBase == null}
          title={
            parsedBase == null
              ? "Saisir un prix de base valide d’abord"
              : "Appliquer le prix de base sur une plage de dates"
          }
          style={{
            padding: "8px 14px",
            borderRadius: 7,
            border: `1px solid ${DS.navy}`,
            background: disabled || parsedBase == null ? "#eef1f5" : "#fff",
            color: disabled || parsedBase == null ? "#999" : DS.navy,
            fontSize: 13,
            fontWeight: 600,
            cursor: disabled || parsedBase == null ? "not-allowed" : "pointer",
          }}
        >
          Appliquer le prix de base
        </button>
      </div>

      {suggestion != null && basePrice == null && (
        <div style={{ fontSize: 11, color: "#666", marginTop: 6 }}>
          💡 Suggestion {suggestion}€ (prix le plus fréquent du calendrier). Non
          encore enregistré comme prix de base.
        </div>
      )}

      {error && (
        <div style={{ fontSize: 11, color: "#c0392b", marginTop: 6 }}>
          Erreur chargement : {error}
        </div>
      )}
      {toast && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: toast.kind === "ok" ? "#1d7a46" : "#c0392b",
          }}
        >
          {toast.msg}
        </div>
      )}

      {modalOpen && parsedBase != null && (
        <ApplyBaseModal
          basePrice={parsedBase}
          applyBase={applyBase}
          onClose={() => setModalOpen(false)}
          onApplied={() => {
            refetch();
            if (onApplied) onApplied();
          }}
        />
      )}
    </div>
  );
}

// ── Modal d'application sur plage ──────────────────────────────────────

function ApplyBaseModal({ basePrice, applyBase, onClose, onApplied }) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onClose)
  const [dateFrom, setDateFrom] = useState(todayIso());
  const [dateTo, setDateTo] = useState(todayIso());
  // Toggle : true = remplir les vides seulement (DÉFAUT) ; false = écraser aussi.
  const [fillEmptyOnly, setFillEmptyOnly] = useState(true);
  const [recap, setRecap] = useState(null); // { total_dates, deja_tarifees, value }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null); // résultat push réel

  function setAllFuture() {
    setDateFrom(todayIso());
    setDateTo(horizonIso());
    setRecap(null);
  }

  async function handleConfirmStage() {
    setBusy(true);
    setErr(null);
    setRecap(null);
    const res = await applyBase({
      date_from: dateFrom,
      date_to: dateTo,
      base_price: basePrice,
      overwrite_existing: !fillEmptyOnly,
      confirmStage: false,
    });
    setBusy(false);
    if (!res.ok) {
      setErr(res.error || "inconnue");
      return;
    }
    setRecap({
      total_dates: res.total_dates ?? 0,
      deja_tarifees: res.deja_tarifees ?? 0,
      value: res.value ?? basePrice,
    });
  }

  async function handlePush() {
    setBusy(true);
    setErr(null);
    const res = await applyBase({
      date_from: dateFrom,
      date_to: dateTo,
      base_price: basePrice,
      overwrite_existing: !fillEmptyOnly,
      confirmStage: true,
    });
    setBusy(false);
    if (!res.ok) {
      setErr(res.error || "inconnue");
      return;
    }
    setDone(res);
    onApplied();
  }

  return (
    <div
      role="dialog"
      aria-label="Appliquer le prix de base"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 22,
          width: "min(440px, 92vw)",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
        }}
      >
        <h3 style={{ margin: "0 0 4px", color: DS.navy, fontSize: 17 }}>
          Appliquer le prix de base
        </h3>
        <p style={{ margin: "0 0 14px", fontSize: 13, color: "#555" }}>
          {basePrice}€/nuit appliqué sur la plage choisie (sans verrou — les
          dates restent automatiques). Les dates verrouillées 📌 ne sont jamais
          touchées.
        </p>

        {!done && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 10,
                flexWrap: "wrap",
              }}
            >
              <label
                style={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                <span style={{ fontSize: 12, fontWeight: 600 }}>Du</span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setRecap(null);
                  }}
                  style={{
                    padding: "6px 9px",
                    borderRadius: 6,
                    border: "1px solid #cdd7e3",
                  }}
                />
              </label>
              <label
                style={{ display: "flex", flexDirection: "column", gap: 3 }}
              >
                <span style={{ fontSize: 12, fontWeight: 600 }}>Au</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setRecap(null);
                  }}
                  style={{
                    padding: "6px 9px",
                    borderRadius: 6,
                    border: "1px solid #cdd7e3",
                  }}
                />
              </label>
              <button
                type="button"
                onClick={setAllFuture}
                style={{
                  alignSelf: "flex-end",
                  padding: "6px 11px",
                  borderRadius: 6,
                  border: `1px solid ${DS.navy}`,
                  background: "#fff",
                  color: DS.navy,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Tout le futur
              </button>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  marginBottom: 6,
                }}
              >
                <input
                  type="radio"
                  name="overwrite"
                  checked={fillEmptyOnly}
                  onChange={() => {
                    setFillEmptyOnly(true);
                    setRecap(null);
                  }}
                />
                Remplir les vides seulement{" "}
                <em style={{ color: "#888" }}>(défaut)</em>
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                }}
              >
                <input
                  type="radio"
                  name="overwrite"
                  checked={!fillEmptyOnly}
                  onChange={() => {
                    setFillEmptyOnly(false);
                    setRecap(null);
                  }}
                />
                Écraser aussi les prix auto existants
              </label>
            </div>

            {recap && (
              <div
                style={{
                  padding: "10px 12px",
                  background: "#f0f7ff",
                  border: `1px solid ${DS.sky}`,
                  borderRadius: 8,
                  fontSize: 13,
                  color: DS.navy,
                  marginBottom: 14,
                }}
              >
                <strong>{recap.total_dates}</strong> date
                {recap.total_dates > 1 ? "s" : ""} →{" "}
                <strong>{recap.value}€</strong> (dont{" "}
                <strong>{recap.deja_tarifees}</strong> déjà tarifée
                {recap.deja_tarifees > 1 ? "s" : ""}).
              </div>
            )}

            {err && (
              <div style={{ fontSize: 12, color: "#c0392b", marginBottom: 10 }}>
                Erreur : {err}
              </div>
            )}

            {busy && recap && (
              <div
                style={{ fontSize: 12, color: "#5a6b7d", marginBottom: 10 }}
                data-testid="baseprice-envoi-en-cours"
              >
                Envoi de {recap.total_dates} date
                {recap.total_dates > 1 ? "s" : ""} à Beds24… ne fermez pas
                cette fenêtre.
              </div>
            )}

            <div
              style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}
            >
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "8px 14px",
                  borderRadius: 7,
                  border: "1px solid #cdd7e3",
                  background: "#fff",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Annuler
              </button>
              {!recap ? (
                <button
                  type="button"
                  onClick={handleConfirmStage}
                  disabled={busy || dateFrom > dateTo}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 7,
                    border: "none",
                    background: busy || dateFrom > dateTo ? "#c4ccd6" : DS.navy,
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor:
                      busy || dateFrom > dateTo ? "not-allowed" : "pointer",
                  }}
                >
                  {busy ? "…" : "Calculer le récap"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlePush}
                  disabled={busy || recap.total_dates === 0}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 7,
                    border: "none",
                    background:
                      busy || recap.total_dates === 0
                        ? "#c4ccd6"
                        : DS.terracotta || "#C17B5C",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor:
                      busy || recap.total_dates === 0
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {busy ? "Envoi…" : `Confirmer (${recap.total_dates})`}
                </button>
              )}
            </div>
          </>
        )}

        {done && (
          <div>
            <div
              style={{
                padding: "12px 14px",
                background: "#eafaf0",
                border: "1px solid #a8e0bf",
                borderRadius: 8,
                fontSize: 13,
                color: "#1d7a46",
                marginBottom: 14,
              }}
            >
              ✅ Prix de base appliqué sur{" "}
              <strong>{done.total_dates ?? 0}</strong> date
              {(done.total_dates ?? 0) > 1 ? "s" : ""}
              {done.dry_run
                ? " (DRY_RUN — pas de push Beds24 réel)"
                : " (poussé sur Beds24)"}
              .
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "8px 14px",
                  borderRadius: 7,
                  border: "none",
                  background: DS.navy,
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
